import moment from 'moment';
import type { PlaceholderDynamicColumn, PlaceholderMultiDynamicColumn, PlaceholderRow } from './context/PlaceholderProvider';
import type { IdNameOption } from './types';
import { CLIPBOARD_LIST_SEPARATOR, splitListLabels } from '../../Components/BombiixAGGrid/fillClipboard';
import { cloneDeep } from 'lodash';

/** A reference as the API stores it: populated, or a bare id. */
type IdRef = { _id?: string } | string | null | undefined;

/** The stored format of a custom-attribute date. */
const DATE_VALUE_FORMAT = 'YYYY-MM-DD';

/**
 * The id behind a reference stored either populated or as a bare id.
 * @param val - the reference
 * @returns its id, or undefined when there is none
 */
export const idOf = (val: IdRef): string | undefined => (val && typeof val === 'object' ? val._id : (val as string | undefined));

/**
 * Replaces (or removes, on full deselect) one column's entry in a Multi Custom Attribute selections
 * array. Removing an emptied entry, rather than leaving a `{colId, valId: []}` stub, keeps "nothing
 * selected" meaning "no entry for this colId" everywhere, matching how a fresh row starts out.
 * @param entries - the row's current selections
 * @param colId - the column being replaced
 * @param valIds - the new selection for that column
 * @returns the selections with that column replaced
 */
export const upsertMultiDynamicEntry = (entries: PlaceholderMultiDynamicColumn[], colId: string, valIds: string[]): PlaceholderMultiDynamicColumn[] => {
  const rest = entries.filter((entry) => idOf(entry?.colId as IdRef) !== colId);
  return valIds.length ? [...rest, { colId, valId: valIds }] : rest;
};

/**
 * Whether a row's own product type is within a Multi Custom Attribute label's configured scope
 * (no scope configured = always enabled).
 * @param meta - the label, with its product-type scope
 * @param rowProductTypeId - the row's product type
 * @returns true when the label applies to the row
 */
export const isMultiDynamicEnabledForRow = (meta: { productType: string[] }, rowProductTypeId: string | undefined): boolean =>
  meta.productType.length === 0 || (Boolean(rowProductTypeId) && meta.productType.includes(rowProductTypeId as string));

/** A custom-attribute entry on a product row, with the editing config its cell reads. */
export type ChildDynamicEntry = PlaceholderDynamicColumn & {
  valueText?: string;
  isChecked?: boolean;
  datePickerConfig?: { isEditable?: boolean };
  textFieldConfig?: { isExceedable?: boolean; charLimit?: number; isEditable?: boolean };
};

/** A product (child) row as a fill writes it: the placeholder's product entry plus its per-row option lists. */
export type ChildFillRow = PlaceholderRow & Record<string, unknown>;

/** The option lists a child fill checks dropped values against. */
export interface ChildFillMeta {
  monthOptions: IdNameOption[];
  dynamicMeta: Array<{ _id: string; options: Array<{ _id: string; isActive?: boolean }> }>;
  multiDynamicMeta: Array<{ _id: string; options: Array<{ _id: string }>; productType: string[] }>;
}

/**
 * The option a dropped value stands for: populated or a bare id from a fill, or the label a copy put
 * on the clipboard, matched without regard to case on the option's name, or its keyName for the
 * custom-attribute options that carry no name.
 * @param options - the options the target row offers
 * @param value - the dropped value
 * @returns the matching option, or undefined when the row does not offer it
 */
export const matchOption = <T extends { _id?: string; name?: string; keyName?: string }>(options: T[] | undefined, value: unknown): T | undefined => {
  const list = options ?? [];
  const id = idOf(value as IdRef);
  const byId = id ? list.find((option) => String(option?._id) === String(id)) : undefined;
  if (byId) return byId;
  const label = value !== null && typeof value === 'object' ? (value as { name?: unknown; keyName?: unknown }).name ?? (value as { keyName?: unknown }).keyName : value;
  const wanted = String(label ?? '')
    .trim()
    .toLowerCase();
  return wanted ? list.find((option) => String(option?.name ?? option?.keyName ?? '').trim().toLowerCase() === wanted) : undefined;
};

/**
 * Applies one dropped value to a product row's custom attribute, following the rules of the cell
 * that edits it: a dropdown or radio takes only an offered option, a date must be a stored-format
 * date and editable, and free text obeys its editable flag and character limit.
 * @param child - the product row, mutated in place
 * @param colId - the attribute column
 * @param newValue - the dropped value
 * @param meta - the attribute option lists
 * @returns true when the row changed
 */
function applyChildDynamicFill(child: ChildFillRow, colId: string, newValue: unknown, meta: ChildFillMeta): boolean {
  const list = (child.dynamicColumn || []) as ChildDynamicEntry[];
  const entry = list.find((item) => item?._id === colId);
  if (!entry || !entry.isActive || !entry.isChecked) return false;
  const attrType = (entry.type ?? (entry.colId as { type?: string } | undefined)?.type) as string | undefined;
  const ownOptions = entry.options as Array<{ _id: string; isActive?: boolean }> | undefined;
  const configOptions = meta.dynamicMeta.find((item) => item._id === colId)?.options;
  const options = (ownOptions?.length ? ownOptions : (configOptions ?? [])).filter((option) => option.isActive !== false);
  const target = entry as Record<string, unknown>;
  if (attrType === 'dropdown') {
    const option = matchOption(options, newValue);
    if (!option) return false;
    target.valueId = option;
    return true;
  }
  if (attrType === 'radio-button') {
    const option = matchOption(options, newValue);
    if (!option) return false;
    target.valueId = String(option._id);
    return true;
  }
  const text = newValue == null ? '' : String(newValue);
  if (attrType === 'date-picker') {
    if (entry.datePickerConfig?.isEditable === false) return false;
    if (text !== '' && !moment(text, DATE_VALUE_FORMAT, true).isValid()) return false;
    target.valueText = text;
    return true;
  }
  const textConfig = entry.textFieldConfig;
  if (!textConfig?.isEditable) return false;
  target.valueText = !textConfig.isExceedable && textConfig.charLimit ? text.slice(0, textConfig.charLimit) : text;
  return true;
}

/**
 * Applies one fill-handle drop onto a product (child) row in place, returning whether anything
 * changed. Only the cells a product row edits take a value: Category 4 from the row's own list,
 * Month from the shared list, and the custom attributes. A dropdown is never cleared by a fill, and
 * a value the row does not offer is refused.
 * @param child - the product row, mutated in place
 * @param field - the grid column the value was dropped on
 * @param newValue - the dropped value
 * @param meta - the option lists to check against
 * @returns true when the row changed
 */
export function applyChildFill(child: ChildFillRow, field: string, newValue: unknown, meta: ChildFillMeta): boolean {
  const target = child as Record<string, unknown>;
  if (field === 'catFourId') {
    const option = matchOption(child.cat4Data as IdNameOption[] | undefined, newValue);
    if (!option) return false;
    target.catFourId = option;
    return true;
  }
  if (field === 'month') {
    const option = matchOption(meta.monthOptions, newValue);
    if (!option) return false;
    target.month = option;
    return true;
  }
  if (field.startsWith('dyn:')) return applyChildDynamicFill(child, field.slice(4), newValue, meta);
  if (field.startsWith('mdyn:')) {
    const colId = field.slice(5);
    const label = meta.multiDynamicMeta.find((item) => item._id === colId);
    if (!label || !isMultiDynamicEnabledForRow(label, idOf(child.supplierType as IdRef))) return false;
    const ids = multiDynamicIdsOf(newValue, label.options);
    if (!ids) return false;
    target.multiCustomAttribute = upsertMultiDynamicEntry((child.multiCustomAttribute || []) as PlaceholderMultiDynamicColumn[], colId, ids);
    return true;
  }
  return false;
}

/** Master-row dropdown columns a fill or paste writes, parents ahead of the columns they reset. */
export const MASTER_SELECT_FILL_ORDER: ReadonlyArray<string> = ['productTypeId', 'catOneId', 'catTwoId', 'catThreeId', 'catFourId', 'brandId', 'subBrandId', 'seasonId', 'month', 'year'];

/** A placeholder (master) row as a fill writes it. */
export type MasterFillRow = PlaceholderRow & Record<string, unknown>;

/** What a master-row fill checks a dropped value against, and how it rebuilds the category lists. */
export interface MasterFillContext {
  optionsFor: (row: MasterFillRow, field: string) => IdNameOption[];
  catBasedOnPTData: boolean;
  category3OptionsFor: (cat1Id: string, cat2Id: string) => unknown[];
  category4OptionsFor: (cat1Id: string, cat2Id: string, cat3Id: string) => unknown[];
}

/**
 * Applies one fill or paste onto a placeholder (master) row's dropdown in place, the way picking the
 * option would: the value must be one the cell offers on that row, it is stored in the shape the cell
 * stores (an option, its id for Sub Brand and Month, its name for Year), and the columns that depend
 * on it are reset as `handleChange` / `handleBrandChange` reset them. An empty value is refused, as
 * the dropdowns cannot be cleared, and so is the value the row already holds, so filling a column
 * never wipes what depends on a cell it leaves as it was.
 * @param row - the placeholder row, mutated in place
 * @param field - the grid column the value was dropped on
 * @param newValue - the dropped value
 * @param context - the row's options and the category lists to rebuild
 * @returns true when the row changed
 */
export function applyMasterFill(row: MasterFillRow, field: string, newValue: unknown, context: MasterFillContext): boolean {
  if (!MASTER_SELECT_FILL_ORDER.includes(field)) return false;
  const option = matchOption(context.optionsFor(row, field), newValue);
  if (!option) return false;
  const target = row as Record<string, unknown>;
  const stored = storedMasterValue(field, option);
  const currentId = idOf(target[field] as IdRef);
  if (currentId !== undefined && currentId !== null && String(currentId) === String(idOf(stored as IdRef))) return false;
  target[field] = stored;
  resetMasterDependents(target, field, context);
  return true;
}

/**
 * A placeholder quantity a fill or paste may write: a whole number, or empty as a cleared input is.
 * @param value - the dropped value
 * @returns the quantity text, or undefined when the input would refuse it
 */
export function acceptPlaceholderQuantity(value: unknown): string | undefined {
  const text = value === null || value === undefined ? '' : String(value).trim();
  return /^\d*$/.test(text) ? text : undefined;
}

/**
 * The value a master dropdown stores for a picked option.
 * @param field - the dropdown column
 * @param option - the picked option
 * @returns the stored value
 */
function storedMasterValue(field: string, option: IdNameOption): unknown {
  if (field === 'subBrandId' || field === 'month') return option._id ?? null;
  if (field === 'year') return option.name ?? null;
  return option;
}

/**
 * Resets what a master dropdown change invalidates, mirroring the grid's own change handlers: a new
 * brand drops the sub-brand; a new product type drops the custom attributes and, when categories
 * follow product type, every category; a category drops the categories below it and rebuilds the
 * next level's option list.
 * @param target - the placeholder row, mutated in place
 * @param field - the dropdown column that changed
 * @param context - the category lists to rebuild from
 */
function resetMasterDependents(target: Record<string, unknown>, field: string, context: MasterFillContext): void {
  if (field === 'brandId') target.subBrandId = null;
  if (field === 'productTypeId') {
    if (context.catBasedOnPTData) Object.assign(target, { catOneId: null, catTwoId: null, catThreeId: null, catFourId: null, category3Options: [], category4Options: [] });
    Object.assign(target, { dynamicColumn: [], multiCustomAttribute: [] });
  }
  if (field === 'catOneId') target.catTwoId = null;
  if (field === 'catOneId' || field === 'catTwoId') Object.assign(target, { catThreeId: null, catFourId: null });
  if (!['catOneId', 'catTwoId', 'catThreeId'].includes(field)) return;
  const cat1 = idOf(target.catOneId as IdRef);
  const cat2 = idOf(target.catTwoId as IdRef);
  const cat3 = idOf(target.catThreeId as IdRef);
  if (cat1 && cat2 && cat3) target.category4Options = context.category4OptionsFor(cat1, cat2, cat3);
  else if (cat1 && cat2) target.category3Options = context.category3OptionsFor(cat1, cat2);
}

/**
 * The option ids a fill or paste writes into a Multi Custom Attribute cell. A drag carries ids,
 * kept when the label offers them; a paste carries the labels a copy joined, each of which must
 * match one of the label's options, otherwise the cell is left as it was. Anything else clears it.
 * @param value - the dropped value
 * @param options - the label's options
 * @returns the ids to store, or undefined when a pasted label matches no option
 */
export function multiDynamicIdsOf(value: unknown, options: Array<{ _id: string; keyName?: string }>): string[] | undefined {
  if (Array.isArray(value)) {
    const known = new Set(options.map((option) => option._id));
    return value.map((entry) => String(idOf(entry as IdRef))).filter((id) => known.has(id));
  }
  if (typeof value !== 'string' || !value.trim()) return [];
  const matched = splitListLabels(value, CLIPBOARD_LIST_SEPARATOR, (label) => matchOption(options, label));
  return matched.every(Boolean) ? matched.map((option) => String(option?._id)) : undefined;
}

/** A row whose custom attributes a fill must reload, and the custom-attribute values it carried for them. */
export interface AttributeReload {
  gridId: string;
  productType: string | undefined;
  brand: string | undefined;
  fills: Array<{ field: string; newValue: unknown }>;
}

/**
 * The id the grid knows a placeholder row by.
 * @param row - the placeholder row
 * @returns its grid id
 */
export function gridIdOf(row: PlaceholderRow & { __gridId?: string }): string {
  return String(row.__gridId ?? row._id ?? '');
}

/**
 * Where a filled column is applied within one fill: the dropdowns in parent-first order, then every
 * other column, so a custom attribute lands after the product type that decides which attributes apply.
 * @param field - the filled column
 * @returns its position
 */
export function masterFillRank(field: string): number {
  const rank = MASTER_SELECT_FILL_ORDER.indexOf(field);
  return rank === -1 ? MASTER_SELECT_FILL_ORDER.length : rank;
}

/**
 * Loads a row's custom attributes for its product type and brand, as picking a product type does:
 * the attributes start empty.
 * @param reload - the product type and brand to load for
 * @param fetchAttributes - loads the attributes for a product type and brand
 * @returns the attributes, or null when the row has no product type or no brand to load them for
 */
async function fetchAttributeList(reload: AttributeReload, fetchAttributes: (productType: string, brand: string) => Promise<unknown>): Promise<PlaceholderDynamicColumn[] | null> {
  if (!reload.productType || !reload.brand) return null;
  const res = (await fetchAttributes(reload.productType, reload.brand)) as { data?: Array<Record<string, unknown>> } | undefined;
  return (res?.data ?? []).map((attr) => ({ ...attr, valueText: '', selectedOpt: null })) as PlaceholderDynamicColumn[];
}

/**
 * Reloads the custom attributes of the rows a fill gave a new product type, then applies the
 * custom-attribute values the same fill carried. The result is built on the rows as they are once the
 * attributes arrive, so edits made meanwhile survive; a row whose product type or brand changed again
 * meanwhile is left alone, since that change loads its own attributes.
 * @param reloads - the rows to reload, by grid id, with the values to apply afterwards
 * @param latestRows - the placeholder rows as they are now
 * @param fetchAttributes - loads the attributes for a product type and brand
 * @param applyFill - applies one custom-attribute value to a row, returning whether it changed
 * @returns the rows to commit, or null when no row took new attributes
 */
export async function withReloadedAttributes<TRow extends PlaceholderRow & { __gridId?: string }>(
  reloads: AttributeReload[],
  latestRows: () => TRow[],
  fetchAttributes: (productType: string, brand: string) => Promise<unknown>,
  applyFill: (row: TRow, field: string, newValue: unknown) => boolean,
): Promise<TRow[] | null> {
  const lists = await Promise.all(reloads.map((reload) => fetchAttributeList(reload, fetchAttributes)));
  const rows: TRow[] = cloneDeep(latestRows());
  let changed = false;
  reloads.forEach((reload, position) => {
    const list = lists[position];
    const row = rows.find((candidate) => gridIdOf(candidate) === reload.gridId);
    if (!list || !row || idOf(row.productTypeId) !== reload.productType || idOf(row.brandId) !== reload.brand) return;
    row.dynamicColumn = list;
    reload.fills.forEach((fill) => applyFill(row, fill.field, fill.newValue));
    changed = true;
  });
  return changed ? rows : null;
}
